import type { RBACConfig } from "@/lib/rbac/types"

// Define all available permissions
export const PERMISSIONS = {
  // Page permissions
  PAGE_VIEW: "view",

  // Component permissions
  COMPONENT_VIEW: "view",
  COMPONENT_CREATE: "create",
  COMPONENT_UPDATE: "update",
  COMPONENT_DELETE: "delete",
  COMPONENT_APPROVE: "approve",
  COMPONENT_REJECT: "reject",
  COMPONENT_REQUEST_MODIFICATION: "request-modification",
  COMPONENT_SELECT_PEOPLE: "select-people",

  // Attribute permissions
  ATTRIBUTE_VIEW: "view",
  ATTRIBUTE_EDIT: "edit",
}

// Define all available pages
export const PAGES = {
  DASHBOARD: "dashboard",
  PROFILE: "profile",
  PEOPLE: "people",
  PAYSLIPS: "payslips",
  CONTRACT_TYPES: "contract-types",
  CONTRACTOR_PAYMENTS: "contractor-payments",
  EXCHANGE_RATE: "exchange-rates",
  ORGANIZATION: "organization",
  TRANSACTIONS: "transactions",
  ONBOARDING: "onboarding",
  LEADERSHIP: "leadership",
  ROLES: "roles",
  CALENDAR: "calendar",
  
  // Workforce module pages
  WORKFORCE_DASHBOARD: "workforce-dashboard",
  WORKFORCE_EMPLOYEES: "workforce-employees",
  WORKFORCE_EMPLOYEES_NEW: "workforce-employees-new",
  WORKFORCE_EMPLOYEES_EDIT: "workforce-employees-edit",
  WORKFORCE_DEPARTMENTS: "workforce-departments",
  WORKFORCE_ROLES: "workforce-roles",
  WORKFORCE_PAYROLL: "workforce-payroll",

  // Performance module pages
  PERFORMANCE: "performance",
  PERFORMANCE_FEEDBACKS: "performance-feedbacks",
  PERFORMANCE_AVALIACOES: "performance-avaliacoes",
  PERFORMANCE_INDIVIDUAL: "performance-individual",
  PERFORMANCE_CLIMA: "performance-clima",
  PERFORMANCE_SETTINGS: "performance-settings",
  PERFORMANCE_SETTINGS_MATRIZ: "performance-settings-matriz",
  PERFORMANCE_SETTINGS_PARAMETROS: "performance-settings-parametros",
  PERFORMANCE_AVALIACOES_INDIVIDUAIS: "performance-avaliacoes-individuais",
  PERFORMANCE_OBJETIVOS_INDIVIDUAIS: "performance-objetivos-individuais",
  PERFORMANCE_BONIFICACAO: "performance-bonificacao",
}

// Pages allowed for all user roles
export const COMMON_PAGES = [ PAGES.DASHBOARD,
  PAGES.DASHBOARD,
]

// Define all available attributes
export const ATTRIBUTES = {
  CLIENT_FINANCIAL_DATA: "client-financial-data",
  EMPLOYEE_SALARY: "employee-salary",
  PAYROLL_DATA: "payroll-data",
  FINANCIAL_SETTINGS: "financial-settings",
}

// Define all available components
export const COMPONENTS = {
  PEOPLE_PAGE: "people-page",
  TRANSACTION_TEAM_VIEW: "transaction-team-view",
  TRANSACTION_FORM: "transaction-form",
  PAYMENT_USER_SELECTOR: "payment-user-selector",
  EXCHANGE_RATE: "exchange-rates",
  TRANSACTIONS_DASHBOARD: "transactions-dashboard",
  EDIT_APPROVED_TRANSACTIONS: "edit-approved-transactions",
  TASK_CARD: "task-card",
  TASK_WIZARD: "task-wizard",
  SLA_CHIP: "sla-chip",
  ROLE_GUARD: "role-guard",
  KPI_WIDGETS: "kpi-widgets",
  TASK_TABLE: "task-table",
  PROCESS_TIMELINE: "process-timeline",
  MONTHLY_PAYABLE_DOWNLOAD: "monthly-payable-download",
  SHARE_UPLOAD: "share-upload",
  SHARE_LIST: "share-list",
  SHARE_ANALYTICS: "share-analytics",
  MSA_LIST: "msa-list",
  MSA_FORM: "msa-form",
  CLIENT_INVOICE_LIST: "client-invoice-list",
  CLIENT_INVOICE_VIEW: "client-invoice-view",
  CLIENT_INVOICE_SEND: "client-invoice-send",
  CLIENT_INVOICE_CREATE_ADHOC: "client-invoice-create-adhoc",
  REVFINOPS_PAYABLES_LIST: "revfinops-payables-list",
  REVFINOPS_PAYABLES_APPROVE: "revfinops-payables-approve",
  REVFINOPS_PAYABLES_SCHEDULE: "revfinops-payables-schedule",
  REVFINOPS_PAYABLES_VIEW_AUDIT: "revfinops-payables-view-audit",
  BANK_FEED_LIST: "bank-feed-list",
  BANK_FEED_UPLOAD: "bank-feed-upload",
  BANK_FEED_VIEW: "bank-feed-view",
  EXTERNAL_TRANSACTIONS_LIST: "external-transactions-list",
  EXTERNAL_TRANSACTIONS_RECONCILE: "external-transactions-reconcile",
  EXTERNAL_TRANSACTIONS_REJECT: "external-transactions-reject",
  RECONCILIATION_DASHBOARD: "reconciliation-dashboard",
  RECONCILIATION_LIST: "reconciliation-list",
  RECONCILIATION_MATCH: "reconciliation-match",
  RECONCILIATION_CREATE_ADHOC: "reconciliation-create-adhoc",
  RECONCILIATION_CREATE_REACTIVE: "reconciliation-create-reactive",
  STRATEGIC_OBJECTIVES: "strategic-objectives",
}

// Define RBAC configuration
export const RBAC_CONFIG: RBACConfig = {
  defaultPageAccess: "deny",
  defaultComponentAccess: "deny",
  defaultAttributeAccess: "deny",

  roles: {
    admin: {
      id: "admin",
      name: "Admin",
      permissions: [
        // Admin has access to all pages
        ...Object.values(PAGES).map((page) => ({
          resource: page,
          resourceType: "page" as const,
          action: PERMISSIONS.PAGE_VIEW,
        })),
        {
          resource: COMPONENTS.TRANSACTION_TEAM_VIEW,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
        {
          resource: COMPONENTS.TRANSACTION_FORM,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_SELECT_PEOPLE,
        },
        {
          resource: COMPONENTS.PAYMENT_USER_SELECTOR,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
        {
          resource: COMPONENTS.TRANSACTION_TEAM_VIEW,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_APPROVE,
        },
        {
          resource: COMPONENTS.TRANSACTION_TEAM_VIEW,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_REJECT,
        },
        {
          resource: COMPONENTS.TRANSACTION_TEAM_VIEW,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_REQUEST_MODIFICATION,
        },
        {
          resource: COMPONENTS.EXCHANGE_RATE,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_CREATE,
        },
        {
          resource: COMPONENTS.EXCHANGE_RATE,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_UPDATE,
        },
        {
          resource: COMPONENTS.EXCHANGE_RATE,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_DELETE,
        },
        {
          resource: COMPONENTS.TRANSACTIONS_DASHBOARD,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
        {
          resource: COMPONENTS.EDIT_APPROVED_TRANSACTIONS,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_UPDATE,
        },
        // PeopleCultureOS components
        ...Object.values(COMPONENTS)
          .filter(component => 
            [COMPONENTS.TASK_CARD, COMPONENTS.TASK_WIZARD, COMPONENTS.SLA_CHIP, 
             COMPONENTS.ROLE_GUARD, COMPONENTS.KPI_WIDGETS, COMPONENTS.TASK_TABLE, 
             COMPONENTS.PROCESS_TIMELINE].includes(component)
          )
          .map(component => ({
            resource: component,
            resourceType: "component" as const,
            action: PERMISSIONS.COMPONENT_VIEW,
          })),
        // Access to monthly payable download component
        {
          resource: COMPONENTS.MONTHLY_PAYABLE_DOWNLOAD,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
        // Shares components - admin has full access
        {
          resource: COMPONENTS.SHARE_UPLOAD,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_CREATE,
        },
        {
          resource: COMPONENTS.SHARE_LIST,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
        {
          resource: COMPONENTS.SHARE_ANALYTICS,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
        {
          resource: PAGES.ROLES,
          resourceType: "page" as const,
          action: PERMISSIONS.PAGE_VIEW,
        },
        // MSA components - admin has full access
        {
          resource: COMPONENTS.MSA_LIST,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
        {
          resource: COMPONENTS.MSA_FORM,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_CREATE,
        },
        {
          resource: COMPONENTS.MSA_FORM,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_UPDATE,
        },
        {
          resource: COMPONENTS.MSA_LIST,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_DELETE,
        },
        // Client Invoice components - admin has full access
        {
          resource: COMPONENTS.CLIENT_INVOICE_LIST,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
        {
          resource: COMPONENTS.CLIENT_INVOICE_VIEW,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
        {
          resource: COMPONENTS.CLIENT_INVOICE_SEND,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_CREATE,
        },
        {
          resource: COMPONENTS.CLIENT_INVOICE_CREATE_ADHOC,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_CREATE,
        },
        // RevFinOps Payables components - admin has full access
        {
          resource: COMPONENTS.REVFINOPS_PAYABLES_LIST,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
        {
          resource: COMPONENTS.REVFINOPS_PAYABLES_APPROVE,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_APPROVE,
        },
        {
          resource: COMPONENTS.REVFINOPS_PAYABLES_SCHEDULE,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_UPDATE,
        },
        {
          resource: COMPONENTS.REVFINOPS_PAYABLES_VIEW_AUDIT,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
        // Bank Feed components - admin has full access
        {
          resource: COMPONENTS.BANK_FEED_LIST,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
        {
          resource: COMPONENTS.BANK_FEED_UPLOAD,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_CREATE,
        },
        {
          resource: COMPONENTS.BANK_FEED_VIEW,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
        // External Transactions components - admin has full access
        {
          resource: COMPONENTS.EXTERNAL_TRANSACTIONS_LIST,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
        {
          resource: COMPONENTS.EXTERNAL_TRANSACTIONS_RECONCILE,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_UPDATE,
        },
        {
          resource: COMPONENTS.EXTERNAL_TRANSACTIONS_REJECT,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_UPDATE,
        },
        // Reconciliation components - admin has full access
        {
          resource: COMPONENTS.RECONCILIATION_DASHBOARD,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
        {
          resource: COMPONENTS.RECONCILIATION_LIST,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
        {
          resource: COMPONENTS.RECONCILIATION_MATCH,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_UPDATE,
        },
        {
          resource: COMPONENTS.RECONCILIATION_CREATE_ADHOC,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_CREATE,
        },
        {
          resource: COMPONENTS.RECONCILIATION_CREATE_REACTIVE,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_CREATE,
        },
        // Attribute permissions for admin
        {
          resource: ATTRIBUTES.CLIENT_FINANCIAL_DATA,
          resourceType: "attribute" as const,
          action: PERMISSIONS.ATTRIBUTE_VIEW,
        },
        {
          resource: ATTRIBUTES.EMPLOYEE_SALARY,
          resourceType: "attribute" as const,
          action: PERMISSIONS.ATTRIBUTE_VIEW,
        },
        {
          resource: ATTRIBUTES.EMPLOYEE_SALARY,
          resourceType: "attribute" as const,
          action: PERMISSIONS.ATTRIBUTE_EDIT,
        },
        {
          resource: ATTRIBUTES.PAYROLL_DATA,
          resourceType: "attribute" as const,
          action: PERMISSIONS.ATTRIBUTE_VIEW,
        },
        {
          resource: ATTRIBUTES.PAYROLL_DATA,
          resourceType: "attribute" as const,
          action: PERMISSIONS.ATTRIBUTE_EDIT,
        },
      ],
    },
    ceo: {
      id: "ceo",
      name: "CEO",
      permissions: [
        // CEO has access to all pages
        ...Object.values(PAGES).map((page) => ({
          resource: page,
          resourceType: "page" as const,
          action: PERMISSIONS.PAGE_VIEW,
        })),
        // CEO has full component access
        ...Object.values(COMPONENTS).map((component) => ({
          resource: component,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        })),
        // CEO can approve, create, update, delete
        {
          resource: COMPONENTS.TRANSACTION_TEAM_VIEW,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_APPROVE,
        },
        {
          resource: COMPONENTS.EXCHANGE_RATE,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_CREATE,
        },
        {
          resource: COMPONENTS.EXCHANGE_RATE,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_UPDATE,
        },
        {
          resource: COMPONENTS.EXCHANGE_RATE,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_DELETE,
        },
        // Attribute permissions for CEO (full access like admin)
        {
          resource: ATTRIBUTES.EMPLOYEE_SALARY,
          resourceType: "attribute" as const,
          action: PERMISSIONS.ATTRIBUTE_VIEW,
        },
        {
          resource: ATTRIBUTES.EMPLOYEE_SALARY,
          resourceType: "attribute" as const,
          action: PERMISSIONS.ATTRIBUTE_EDIT,
        },
        {
          resource: ATTRIBUTES.PAYROLL_DATA,
          resourceType: "attribute" as const,
          action: PERMISSIONS.ATTRIBUTE_VIEW,
        },
        {
          resource: ATTRIBUTES.PAYROLL_DATA,
          resourceType: "attribute" as const,
          action: PERMISSIONS.ATTRIBUTE_EDIT,
        },
      ],
    },
    "vice-presidente": {
      id: "vice-presidente",
      name: "Vice Presidente",
      permissions: [
        // Vice Presidente has access to all pages like CEO (except some sensitive ones)
        ...Object.values(PAGES)
          .filter((page) => ![PAGES.PAYSLIPS].includes(page))
          .map((page) => ({
            resource: page,
            resourceType: "page" as const,
            action: PERMISSIONS.PAGE_VIEW,
          })),
        // VP component permissions - similar to CEO
        ...Object.values(COMPONENTS).map((component) => ({
          resource: component,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        })),
        {
          resource: COMPONENTS.TRANSACTION_TEAM_VIEW,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_APPROVE,
        },
        {
          resource: COMPONENTS.EXCHANGE_RATE,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_CREATE,
        },
        {
          resource: COMPONENTS.EXCHANGE_RATE,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_UPDATE,
        },
        // Attribute permissions for VP (full salary view, limited edit)
        {
          resource: ATTRIBUTES.EMPLOYEE_SALARY,
          resourceType: "attribute" as const,
          action: PERMISSIONS.ATTRIBUTE_VIEW,
        },
        {
          resource: ATTRIBUTES.PAYROLL_DATA,
          resourceType: "attribute" as const,
          action: PERMISSIONS.ATTRIBUTE_VIEW,
        },
      ],
    },
    "diretor-executivo": {
      id: "diretor-executivo",
      name: "Diretor Executivo",
      permissions: [
        // Diretor Executivo has access to most pages
        ...Object.values(PAGES)
          .filter((page) => ![PAGES.PAYSLIPS].includes(page))
          .map((page) => ({
            resource: page,
            resourceType: "page" as const,
            action: PERMISSIONS.PAGE_VIEW,
          })),
        // Director component permissions
        {
          resource: COMPONENTS.TRANSACTION_TEAM_VIEW,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
        {
          resource: COMPONENTS.TRANSACTION_TEAM_VIEW,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_APPROVE,
        },
        {
          resource: COMPONENTS.MONTHLY_PAYABLE_DOWNLOAD,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
        // Attribute permissions for Director (limited salary view, no edit)
        {
          resource: ATTRIBUTES.EMPLOYEE_SALARY,
          resourceType: "attribute" as const,
          action: PERMISSIONS.ATTRIBUTE_VIEW,
        },
      ],
    },
    "diretor-financeiro": {
      id: "diretor-financeiro",
      name: "Diretor Financeiro",
      permissions: [
        // Diretor Financeiro has same pages as Diretor Executivo (all except payslips)
        ...Object.values(PAGES)
          .filter((page) => ![PAGES.PAYSLIPS].includes(page))
          .map((page) => ({
            resource: page,
            resourceType: "page" as const,
            action: PERMISSIONS.PAGE_VIEW,
          })),
        // Same component permissions as Diretor Executivo
        {
          resource: COMPONENTS.TRANSACTION_TEAM_VIEW,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
        {
          resource: COMPONENTS.TRANSACTION_TEAM_VIEW,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_APPROVE,
        },
        {
          resource: COMPONENTS.MONTHLY_PAYABLE_DOWNLOAD,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
        // Attribute permissions (limited salary view, no edit)
        {
          resource: ATTRIBUTES.EMPLOYEE_SALARY,
          resourceType: "attribute" as const,
          action: PERMISSIONS.ATTRIBUTE_VIEW,
        },
      ],
    },
    rh: {
      id: "rh",
      name: "Human Resources",
      permissions: [
        // HR has access to workforce and people-related pages
        ...Object.values(PAGES)
          .filter((page) =>
            [
              PAGES.DASHBOARD,
              PAGES.PROFILE,
              PAGES.PEOPLE,
              PAGES.PAYSLIPS,
              PAGES.WORKFORCE_DASHBOARD,
              PAGES.WORKFORCE_EMPLOYEES,
              PAGES.WORKFORCE_EMPLOYEES_NEW,
              PAGES.WORKFORCE_EMPLOYEES_EDIT,
              PAGES.WORKFORCE_DEPARTMENTS,
              PAGES.WORKFORCE_ROLES,
              PAGES.WORKFORCE_PAYROLL,
              // Performance module pages
              PAGES.PERFORMANCE,
              PAGES.PERFORMANCE_FEEDBACKS,
              PAGES.PERFORMANCE_AVALIACOES,
              PAGES.PERFORMANCE_INDIVIDUAL,
              PAGES.PERFORMANCE_CLIMA,
              PAGES.PERFORMANCE_SETTINGS,
              PAGES.PERFORMANCE_SETTINGS_MATRIZ,
              PAGES.PERFORMANCE_SETTINGS_PARAMETROS,
              PAGES.PERFORMANCE_AVALIACOES_INDIVIDUAIS,
              PAGES.PERFORMANCE_OBJETIVOS_INDIVIDUAIS,
              PAGES.PERFORMANCE_BONIFICACAO,
            ].includes(page)
          )
          .map((page) => ({
            resource: page,
            resourceType: "page" as const,
            action: PERMISSIONS.PAGE_VIEW,
          })),
        // Full salary permissions for HR
        {
          resource: ATTRIBUTES.EMPLOYEE_SALARY,
          resourceType: "attribute" as const,
          action: PERMISSIONS.ATTRIBUTE_VIEW,
        },
        {
          resource: ATTRIBUTES.EMPLOYEE_SALARY,
          resourceType: "attribute" as const,
          action: PERMISSIONS.ATTRIBUTE_EDIT,
        },
        {
          resource: ATTRIBUTES.PAYROLL_DATA,
          resourceType: "attribute" as const,
          action: PERMISSIONS.ATTRIBUTE_VIEW,
        },
        {
          resource: ATTRIBUTES.PAYROLL_DATA,
          resourceType: "attribute" as const,
          action: PERMISSIONS.ATTRIBUTE_EDIT,
        },
      ],
    },
    "analista-rh": {
      id: "analista-rh",
      name: "HR Analyst",
      permissions: [
        // HR Analyst has access to workforce and people-related pages (same as HR)
        ...Object.values(PAGES)
          .filter((page) =>
            [
              PAGES.DASHBOARD,
              PAGES.PROFILE,
              PAGES.PEOPLE,
              PAGES.PAYSLIPS,
              PAGES.WORKFORCE_DASHBOARD,
              PAGES.WORKFORCE_EMPLOYEES,
              PAGES.WORKFORCE_EMPLOYEES_NEW,
              PAGES.WORKFORCE_EMPLOYEES_EDIT,
              PAGES.WORKFORCE_DEPARTMENTS,
              PAGES.WORKFORCE_ROLES,
              PAGES.WORKFORCE_PAYROLL,
              // Performance module pages
              PAGES.PERFORMANCE,
              PAGES.PERFORMANCE_FEEDBACKS,
              PAGES.PERFORMANCE_AVALIACOES,
              PAGES.PERFORMANCE_INDIVIDUAL,
              PAGES.PERFORMANCE_CLIMA,
              PAGES.PERFORMANCE_SETTINGS,
              PAGES.PERFORMANCE_SETTINGS_MATRIZ,
              PAGES.PERFORMANCE_SETTINGS_PARAMETROS,
              PAGES.PERFORMANCE_BONIFICACAO,
            ].includes(page)
          )
          .map((page) => ({
            resource: page,
            resourceType: "page" as const,
            action: PERMISSIONS.PAGE_VIEW,
          })),
        // View-only salary permissions for HR Analyst (cannot edit)
        {
          resource: ATTRIBUTES.EMPLOYEE_SALARY,
          resourceType: "attribute" as const,
          action: PERMISSIONS.ATTRIBUTE_VIEW,
        },
        {
          resource: ATTRIBUTES.PAYROLL_DATA,
          resourceType: "attribute" as const,
          action: PERMISSIONS.ATTRIBUTE_VIEW,
        },
      ],
    },
    manager: {
      id: "manager",
      name: "Manager",
      permissions: [
        // Manager has access to workforce pages but not payroll
        ...Object.values(PAGES)
          .filter((page) =>
            [
              PAGES.DASHBOARD,
              PAGES.PROFILE,
              PAGES.PEOPLE,
              PAGES.WORKFORCE_DASHBOARD,
              PAGES.WORKFORCE_EMPLOYEES,
              PAGES.WORKFORCE_DEPARTMENTS,
            ].includes(page)
          )
          .map((page) => ({
            resource: page,
            resourceType: "page" as const,
            action: PERMISSIONS.PAGE_VIEW,
          })),
        // Limited salary view for managers (direct reports only - to be enforced in business logic)
        {
          resource: ATTRIBUTES.EMPLOYEE_SALARY,
          resourceType: "attribute" as const,
          action: PERMISSIONS.ATTRIBUTE_VIEW,
        },
      ],
    },
    "diretor-tecnico": {
      id: "diretor-tecnico",
      name: "Technical Director",
      permissions: [
        // Technical Directors have access to most technical pages including calendar
        ...Object.values(PAGES)
          .filter((page) => ![PAGES.PAYSLIPS, PAGES.CONTRACT_TYPES].includes(page))
          .map((page) => ({
            resource: page,
            resourceType: "page" as const,
            action: PERMISSIONS.PAGE_VIEW,
          })),
        // Technical Director component permissions
        {
          resource: COMPONENTS.TRANSACTION_TEAM_VIEW,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
        {
          resource: COMPONENTS.EXCHANGE_RATE,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_UPDATE,
        },
        {
          resource: COMPONENTS.STRATEGIC_OBJECTIVES,
          resourceType: "component" as const,
          action: PERMISSIONS.COMPONENT_VIEW,
        },
      ],
    },
    employee: {
      id: "employee",
      name: "Employee",
      permissions: [
        // Employee has access to basic pages
        ...Object.values(PAGES)
          .filter((page) =>
            [PAGES.DASHBOARD, PAGES.PROFILE, PAGES.CONTRACTOR_PAYMENTS, PAGES.ORGANIZATION, PAGES.ONBOARDING].includes(page)
          )
          .map((page) => ({
            resource: page,
            resourceType: "page" as const,
            action: PERMISSIONS.PAGE_VIEW,
          })),
      ],
    },
    contractor: {
      id: "contractor",
      name: "Contractor",
      permissions: [
        // Contractor has access to limited pages
        ...Object.values(PAGES)
          .filter((page) =>
            [PAGES.DASHBOARD, PAGES.PROFILE, PAGES.CONTRACTOR_PAYMENTS, PAGES.ONBOARDING].includes(page)
          )
          .map((page) => ({
            resource: page,
            resourceType: "page" as const,
            action: PERMISSIONS.PAGE_VIEW,
          })),
      ],
    },
    guest: {
      id: "guest",
      name: "Guest",
      permissions: [
        // Guest has very limited access
        {
          resource: PAGES.DASHBOARD,
          resourceType: "page" as const,
          action: PERMISSIONS.PAGE_VIEW,
        },
        {
          resource: PAGES.PROFILE,
          resourceType: "page" as const,
          action: PERMISSIONS.PAGE_VIEW,
        },
      ],
    },
    "dev-collaborator": {
      id: "dev-collaborator",
      name: "Developer Collaborator",
      // Princípio de menor privilégio — deny-all por default.
      // Apenas PAGES.PROFILE concedido pra evitar tela em branco no primeiro login.
      // Expansão sob demanda via PR + audit trail no histórico do repo.
      // Hard-no list (NUNCA conceder, mesmo per-user, sem revisão do arch doc):
      //   - ATTRIBUTES.EMPLOYEE_SALARY (view ou edit)
      //   - ATTRIBUTES.PAYROLL_DATA (view ou edit)
      //   - ATTRIBUTES.CLIENT_FINANCIAL_DATA
      //   - ATTRIBUTES.FINANCIAL_SETTINGS
      // Ver: packages/entities/clients/monumenta/monutrack/docs/architecture/STAGE-MT-EXTERN-SALARY-BLIND-ACCESS-2026-05-13.md
      permissions: [
        {
          resource: PAGES.PROFILE,
          resourceType: "page" as const,
          action: PERMISSIONS.PAGE_VIEW,
        },
      ],
    },
    user: {
      id: "user",
      name: "User",
      // Backend platform-user role. Mirrors dev-collaborator (deny-all + PROFILE only)
      // until backend supports custom tenant roles. Same hard-no list applies.
      permissions: [
        {
          resource: PAGES.PROFILE,
          resourceType: "page" as const,
          action: PERMISSIONS.PAGE_VIEW,
        },
      ],
    },
  },
}
